import { useEffect, useState, useRef, useContext } from "react";
import SendIcon from "../assets/Send.svg";
import Logo from "../assets/Logo.svg";
import PositiveIcon from "../assets/positive.svg";
import NegativeIcon from "../assets/negative.svg";
import CopyIcon from "../assets/copy.svg";
import InsufficientCreditsModal from "../components/modals/InsufficientCreditsModal";
import { AuthContext } from "../context/authContext";
import {
  createNewChat,
  postNewMessage,
  getAIResponse,
  checkCredits,
} from "../services/chatService";
import LoadingAnimation from "./LoadingAnimation";
import { MoonLoader, PuffLoader } from "react-spinners";
import { message as antdMessage } from "antd";

function LiveChat({
  chat,
  setIsModalOpen,
  updateMessages,
  updateChats,
  newChat,
  setNewChat,
  setSelectedChatId,
  initials,
  userName,
  setSelectedFeedback,
  toggle,
  chats,
  setSelectedChatMessages
}) {
  const [message, setMessage] = useState("");
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creatingChat, setCreatingChat] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditMessage, setCreditMessage] = useState("");
  const userId = localStorage.getItem("userId");
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const [borderRadius, setBorderRadius] = useState("100px");
  const isSending = useRef(false);
  const { timezone, updateCreditBalance } = useContext(AuthContext);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "44px";
      const scrollHeight = textareaRef.current.scrollHeight;
      const newHeight = Math.max(44, scrollHeight);
      textareaRef.current.style.height = `${newHeight}px`;
      setBorderRadius(newHeight > 44 ? "30px" : "100px");
    }
  }, [message]);

  const scrollToBottom = (behavior = "smooth") => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior });
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chat, isTyping]);

  const handleCopyMessage = (text, messageId) => {
    const cleanText = text
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim();
    navigator.clipboard.writeText(cleanText).then(() => {
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    });
  };

  const handleFeedbackClick = (id, type) => {
    setSelectedFeedback({ id, type });
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!chat) {
      setDefaultMessages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newChat]);

  useEffect(() => {
    if (chats.length === 0) {
      setDefaultMessages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats]);

  const setDefaultMessages = async () => {
    setCreatingChat(true);
    const feedback = { type: "Positive", text: "" };
    const onboarded = localStorage.getItem('onboarding') === 'true';
    const loginMessage = onboarded ? localStorage.getItem('eachTimeLoginMessage') : localStorage.getItem('firstTimeLoginMessage');
    const defaultMessages = onboarded
      ? [
          {
            role: "user",
            text: "Default Message",
            feedback: feedback,
          },
          {
            role: "assistant",
            text: `Hi <strong>${userName}</strong>!<br /><br />${loginMessage}`,
            feedback: feedback,
          },
        ]
      : [
          {
            role: "user",
            text: "Default Message",
            feedback: feedback,
          },
          {
            role: "assistant",
            text: `Hi <strong>${userName}</strong>!<br /><br />${loginMessage}`,
            feedback: feedback,
          },
          {
            role: "assistant",
            text: `If you are wondering what to chat about, here are some sample questions to get us started:<br/><br/>
                1: What's the latest sports news?<br/>
                2: Can you give me updates on my favorite team?<br/>
                3: What are the upcoming game schedules?</i><br/><br/>`,
            feedback: feedback,
          },
          {
            role: "assistant",
            text: `Feel free to ask me anything sports-related, or just drop by to say Hi! Looking forwards to our chats!`,
            feedback: feedback,
          },
        ];

    if (!onboarded) {
      localStorage.setItem('onboarding', true);
    }

    const newChat = { title: "New Chat", userId };

    try {
      const response = await createNewChat(newChat);
      for (let message of defaultMessages) {
        const newMessage = {
          chatId: response.chatId,
          role: message.role,
          text: message.text,
          feedback: message.feedback,
          userId: userId,
        };
        await postNewMessage(response.chatId, newMessage);
        updateMessages(response.chatId);
      }
      updateChats();
      setSelectedChatId(response.chatId);
      setNewChat(!newChat);
    } catch (error) {
      console.log("Error Posting Chat: ", error);
    } finally {
      setCreatingChat(false);
    }
    return "Default Messages Added";
  };

  const postMessage = async (message) => {
    try {
      const res = await postNewMessage(message.chatId, message);
      if (message.isAI) {
        updateCreditBalance(res?.user?.creditBalance);
      }
      return true;
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const { errorCode } = error.response.data;
        if (errorCode === "INSUFFICIENT_CREDITS") {
          setShowCreditModal(true);
          setCreditMessage(error.response.data.message);
          scrollToBottom();
        }
      }
      console.log("Error Posting Message: ", error);
      return false;
    }
  };

  const handleSendMessage = async () => {
    if (isSending.current || !message || !message.trim() || !chat || chat?.length < 2) {
      isSending.current = false;
      return;
    }

    isSending.current = true;
    setLoading(true);

    // Check credits before sending message
    const balance = localStorage.getItem("creditBalance");
    if(balance && balance <= 0){
      try {
        const creditCheck = await checkCredits(userId);
        if (!creditCheck.hasSufficientCredits) {
          setShowCreditModal(true);
          setCreditMessage(creditCheck.message);
          setLoading(false);
          isSending.current = false;
          scrollToBottom();
          return;
        }
      } catch (error) {
        console.error("Error checking credits: ", error);
        antdMessage.error("Failed to check credits. Please try again.");
        setLoading(false);
        isSending.current = false;
        return;
      }
    }

    const feedback = { type: "Positive", text: "" };
    const chatId = chat[0].chatId;

    const userMessage = {
      chatId,
      role: "user",
      text: message,
      feedback,
      userId,
      isAI: false,
    };

    try {
      const updatedChatWithUser = [...chat, userMessage];
      setSelectedChatMessages(updatedChatWithUser);
      setMessage("");
      setIsTyping(true);
      const email = localStorage.getItem("email");
      const payload = {
        query: [
          ...chat
            .map((chatMessage, index) => ({
              role: chatMessage.role,
              content: index === 0 ? "hi" : chatMessage.text,
            }))
            .filter((_, index) => index !== 2 && index !== 3),
          { role: "user", content: message },
        ],
        timezone,
        email
      };

      // Get AI response
      let responseAi;
      try {
        responseAi = await getAIResponse(payload);
      } catch (error) {
        console.error("Error Fetching Response From AI Bot: ", error);
        responseAi = { response: "I'm sorry, I couldn't retrieve accurate results at the moment. Could you please try again in a little while?" };
      }

      // Post AI response
      const aiMessage = {
        chatId,
        role: "assistant",
        text: responseAi.response,
        feedback,
        userId,
        isAI: true,
      };
      await postMessage(userMessage);
      await postMessage(aiMessage);
      setSelectedChatMessages([...updatedChatWithUser, aiMessage]);

    } catch (error) {
      console.error("Error handling message send process: ", error);
    } finally {
      setLoading(false);
      setIsTyping(false);
      isSending.current = false;
    }
  };

  return (
    <div className="w-full h-screen flex flex-col relative bg-white">
      <div
        ref={chatContainerRef}
        className={`flex-grow overflow-y-auto py-2 md:py-4 flex flex-col gap-1 md:gap-2 pb-20 md:pb-24 px-3 md:px-6 lg:px-8 ${
          toggle ? "" : "mt-16 md:mt-0"
        }`}
        style={{ minHeight: "calc(100vh - 80px)", overflowAnchor: "none" }}
      >
        {creatingChat ? (
          <div className="flex justify-center items-center h-full">
            <MoonLoader color="red" size={40}/>
          </div>
        ) : chat && chat.length > 0 ? (
          chat
            .filter((chatMessage) => chatMessage.text !== "Default Message")
            .map((chatMessage) => (
              <div
                key={chatMessage._id}
                className={`flex flex-col md:flex-row p-3 md:p-6 rounded-lg md:rounded-xl transition-all duration-200 ${
                  chatMessage.role === "user"
                    ? "bg-transparent"
                    : "bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <div className="flex flex-1">
                  <div
                    className={`flex-shrink-0 rounded-full w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 flex items-center justify-center text-white font-bold ${
                      chatMessage.role === "user" ? "bg-gray-400" : ""
                    }`}
                  >
                    {chatMessage.role === "user" ? (
                      <span className="flex items-center justify-center text-xs md:text-sm">
                        {initials}
                      </span>
                    ) : (
                      <img
                        src={Logo}
                        alt="Logo"
                        className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6"
                      />
                    )}
                  </div>
                  <div
                    className={`ml-2 md:ml-3 flex-1 text-left text-sm md:text-base ${
                      chatMessage.role === "user"
                        ? "text-gray-600"
                        : "text-gray-800"
                    }`}
                  >
                    {chatMessage.role === "user" ? (
                      <div className="font-medium">{chatMessage.text}</div>
                    ) : (
                      <div
                        className="prose prose-xs md:prose-sm max-w-none text-left"
                        dangerouslySetInnerHTML={{ __html: chatMessage.text }}
                      />
                    )}
                  </div>
                </div>
                {chatMessage.role !== "user" && (
                  <div className="flex flex-row md:flex-col lg:flex-row items-start justify-end mt-1 md:mt-0 space-x-1 md:space-x-0 md:space-y-1 lg:space-y-0 lg:space-x-2 ml-0 md:ml-2">
                    <button
                      onClick={() => handleCopyMessage(chatMessage.text, chatMessage._id)}
                      className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                      title="Copy message"
                    >
                      {copiedMessageId === chatMessage._id ? (
                        <span className="text-xs text-green-500">✓</span>
                      ) : (
                        <img src={CopyIcon} alt="Copy" className="w-3 h-3 md:w-4 md:h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleFeedbackClick(chatMessage._id, "Positive")}
                      className="p-1 text-gray-500 hover:text-green-500 hover:bg-gray-200 rounded-md transition-colors"
                      title="Positive feedback"
                    >
                      <img
                        src={PositiveIcon}
                        alt="Positive"
                        className="w-3 h-3 md:w-4 md:h-4"
                      />
                    </button>
                    <button
                      onClick={() => handleFeedbackClick(chatMessage._id, "Negative")}
                      className="p-1 text-gray-500 hover:text-red-500 hover:bg-gray-200 rounded-md transition-colors"
                      title="Negative feedback"
                    >
                      <img
                        src={NegativeIcon}
                        alt="Negative"
                        className="w-3 h-3 md:w-4 md:h-4"
                      />
                    </button>
                  </div>
                )}
              </div>
            ))
        ) : null}
        {isTyping && (
          <div className="flex flex-col md:flex-row p-3 md:p-6 rounded-lg md:rounded-xl bg-gray-50">
            <div className="flex flex-1">
              <div className="flex-shrink-0 rounded-full w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 flex items-center justify-center">
                <img
                  src={Logo}
                  alt="Logo"
                  className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6"
                />
              </div>
              <LoadingAnimation />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
      <InsufficientCreditsModal
        visible={showCreditModal}
        message={creditMessage}
        onClose={() => setShowCreditModal(false)}
      />
      <div className="sticky bottom-0 bg-white py-4 md:py-4 px-3 md:px-6 lg:px-8 border-t border-gray-200">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!loading) {
              handleSendMessage();
            }
          }}
          className="relative"
        >
          <textarea
            ref={textareaRef}
            className="w-full bg-gray-50 border border-gray-200 text-gray-600 rounded-full py-[8px] pl-[12px] pr-[44px] focus:outline-none focus:border-red-500 resize-none"
            placeholder="Ask a question..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (!loading) {
                  handleSendMessage();
                }
              }
            }}
            required
            style={{
              minHeight: "44px",
              height: "44px",
              borderRadius: borderRadius,
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              lineHeight: "1.5",
              paddingTop: "8px",
              paddingBottom: "8px",
            }}
          />
          <button
            type="submit"
            disabled={loading || !message.trim()}
            className={`cursor-pointer absolute top-1.5 right-2 pt-1 md:right-3 bottom-2 md:bottom-4 p-1 rounded-full ${
              loading || !message.trim()
                ? "text-gray-400"
                : "text-blue-500 hover:bg-blue-50"
            } transition-colors`}
          >
            {loading ? (
              <PuffLoader color="red" size={20} />
            ) : (
              <img src={SendIcon} className="w-6 h-6" alt="Send" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LiveChat;